import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef, useState } from "react";
import { useRemSize } from "../../../../hooks/useRemSize";
import { thousandize } from "../../../../util/formatting";
interface BarChartProps {
  donation: number;
  maximumDeduction: number;
  taxBenefit: number;
  labels?: {
    maximumDeduction?: string;
    yourDonation?: string;
    yourTaxBenefit?: string;
  };
  locale?: string;
}
interface Dimensions {
  width: number;
  height: number;
}
interface Padding {
  left: number;
  right: number;
  top: number;
  bottom: number;
}
export const DKGavebrevTaxBarChart: React.FC<BarChartProps> = t0 => {
  const $ = _c(27);
  const {
    donation,
    maximumDeduction,
    taxBenefit,
    labels,
    locale
  } = t0;
  const containerRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      width: 0,
      height: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [dimensions, setDimensions] = useState(t1);
  const remSize = useRemSize();
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (!containerRef.current) {
        return;
      }
      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const {
            width,
            height
          } = entry.contentRect;
          setDimensions({
            width,
            height
          });
        }
      });
      resizeObserver.observe(containerRef.current);
      return () => resizeObserver.disconnect();
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  const t4 = 3 * remSize;
  let t5;
  if ($[3] !== t4) {
    t5 = {
      left: 0,
      right: 0,
      top: t4,
      bottom: 0
    };
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const padding = t5;
  const chartWidth = Math.max(dimensions.width - padding.left - padding.right, 0);
  const chartHeight = Math.max(dimensions.height - padding.top - padding.bottom, 0);
  let t6;
  if ($[5] !== chartHeight || $[6] !== maximumDeduction) {
    t6 = val => {
      if (maximumDeduction === 0) {
        return 0;
      }
      return val / maximumDeduction * chartHeight;
    };
    $[5] = chartHeight;
    $[6] = maximumDeduction;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const scale = t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      width: "100%",
      height: "100%",
      position: "relative"
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== chartWidth || $[10] !== dimensions.height || $[11] !== dimensions.width || $[12] !== donation || $[13] !== labels?.maximumDeduction || $[14] !== labels?.yourDonation || $[15] !== labels?.yourTaxBenefit || $[16] !== locale || $[17] !== maximumDeduction || $[18] !== padding.bottom || $[19] !== padding.left || $[20] !== padding.top || $[21] !== remSize || $[22] !== scale || $[23] !== taxBenefit) {
    t8 = dimensions.width !== 0 && dimensions.height !== 0 && <svg width={dimensions.width} height={dimensions.height} style={{
      position: "absolute",
      top: 0,
      left: 0,
      overflow: "visible"
    }}><defs><pattern id="diagonalHatch" patternUnits="userSpaceOnUse" width="4" height="4"><path d={"M-1,1 l2,-2\n                     M0,4 l4,-4\n                     M3,5 l2,-2"} style={{
            stroke: "#fafafa",
            strokeWidth: 1
          }} /></pattern></defs><line x1={0} y1={padding.top} x2={0} y2={dimensions.height - padding.bottom} stroke="black" strokeWidth="1" /><line x1={-remSize * 0.5} y1={padding.top + 0.5} x2={0} y2={padding.top + 0.5} stroke="black" strokeWidth="1" /><text x={-remSize * 1} y={padding.top - remSize * 0.6} textAnchor="end" alignmentBaseline="middle">{labels?.maximumDeduction || "Maksimalt fradrag muligt"}</text><text x={-remSize * 1} y={padding.top + remSize * 0.6} textAnchor="end" alignmentBaseline="middle" fontSize="0.75rem">{thousandize(maximumDeduction, locale)} kr</text>{(() => {
        const LABEL_HEIGHT = 2 * remSize;
        const donationY = dimensions.height - padding.bottom - scale(Math.min(donation, maximumDeduction)) + 0.5;
        const benefitY = dimensions.height - padding.bottom - scale(taxBenefit);
        const distance = Math.abs(donationY - benefitY);
        const labelOverlap = distance - LABEL_HEIGHT - remSize * 0.5;
        const isOverlapping = labelOverlap < 0;
        const overlapAdjustment = isOverlapping ? Math.abs(labelOverlap) / 2 : 0;
        return <><g style={{
            transition: "all 0.5s",
            transform: `translateY(${donationY}px)`,
            opacity: donation > 0 && donation < maximumDeduction ? 1 : 0
          }}><line x1={-remSize * 0.5} y1={0} x2={0} y2={0} stroke="black" strokeWidth="1" /><g style={{
              transition: "all 0.5s",
              transform: `translateY(${-overlapAdjustment}px)`
            }}><text x={-remSize * 1} y={-remSize * 0.6} textAnchor="end" alignmentBaseline="middle" paintOrder="stroke" fill="black" stroke="#fafafa" strokeWidth="6">{labels?.yourDonation || "Din donation"}</text><text x={-remSize * 1} y={remSize * 0.6} textAnchor="end" alignmentBaseline="middle" fontSize="0.75rem" paintOrder="stroke" fill="black" stroke="#fafafa" strokeWidth="6">{thousandize(Math.min(donation, maximumDeduction), locale)} kr</text></g></g><g style={{
            transition: "all 0.5s",
            transform: `translateY(${benefitY}px)`,
            opacity: taxBenefit > 0 ? 1 : 0
          }}><line x1={-remSize * 0.5} y1={0} x2={0} y2={0} stroke="black" strokeWidth="1" /><g style={{
              transition: "all 0.5s",
              transform: `translateY(${overlapAdjustment}px)`
            }}><text x={-remSize * 1} y={-remSize * 0.6} textAnchor="end" alignmentBaseline="middle">{labels?.yourTaxBenefit || "Dit fradrag"}</text><text x={-remSize * 1} y={remSize * 0.6} textAnchor="end" alignmentBaseline="middle" fontSize="0.75rem">{thousandize(taxBenefit, locale)} kr</text></g></g></>;
      })()}<rect x={padding.left} y={-(dimensions.height - padding.bottom)} width={Math.min(chartWidth, 160)} height={scale(Math.min(donation, maximumDeduction))} transform="scale(1,-1)" fill="#000" opacity={donation > 0 ? 1 : 0.3} style={{
        transition: "all 0.5s"
      }} />{taxBenefit > 0 && <rect x={padding.left} y={-(dimensions.height - padding.bottom)} width={Math.min(chartWidth, 160)} height={scale(taxBenefit)} transform="scale(1,-1)" fill="url(#diagonalHatch)" style={{
        transition: "all 0.5s"
      }} />}</svg>;
    $[9] = chartWidth;
    $[10] = dimensions.height;
    $[11] = dimensions.width;
    $[12] = donation;
    $[13] = labels?.maximumDeduction;
    $[14] = labels?.yourDonation;
    $[15] = labels?.yourTaxBenefit;
    $[16] = locale;
    $[17] = maximumDeduction;
    $[18] = padding.bottom;
    $[19] = padding.left;
    $[20] = padding.top;
    $[21] = remSize;
    $[22] = scale;
    $[23] = taxBenefit;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t8) {
    t9 = <div ref={containerRef} style={t7}>{t8}</div>;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
};